import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { Employee, WorkSchedule } from '../../types/employee';
import { displayEmployeeName } from '../../utils/workScheduleDisplay';
import { chipTextColorFor } from '../../utils/employeeColors';
import { formatLocalDateString } from '../../utils/dateUtils';

/**
 * 주간 시간 그리드 — 구글 캘린더식 드래그 편집
 *   · 블록 드래그: 세로 = 시간 이동(30분 스냅), 가로 = 요일(날짜) 이동
 *   · 블록 하단 가장자리 드래그: 종료 시간 리사이즈 (최소 30분)
 *   · 클릭(드래그 없이): 수정 폼 열기
 * 자정을 넘는 야간 근무 블록은 24시까지 잘라 표시하고 리사이즈는 막는다.
 */

const HOUR_HEIGHT = 44; // 1시간 픽셀
const SNAP_MIN = 30; // 스냅 단위(분)
const TIME_COL_WIDTH = 52;
const CLICK_TOLERANCE = 4; // px — 이하 움직임은 클릭으로 간주

export interface GridSegment {
  schedule: WorkSchedule;
  /** 템플릿 근무 구간 번호 (커스텀 스케줄은 1) */
  seg: 1 | 2;
  start: Date;
  end: Date;
  /** 자정 넘김으로 24시에 잘렸는지 */
  clipped: boolean;
}

export type GridCommitMode = 'move' | 'resize';

interface WeekTimeGridProps {
  weekStart: Date; // 일요일 00:00
  schedules: WorkSchedule[];
  employees: Employee[];
  employeeColors: Record<string, string>;
  onCommit: (
    segment: GridSegment,
    newStart: Date,
    newEnd: Date,
    mode: GridCommitMode,
  ) => void;
  onEdit: (schedule: WorkSchedule) => void;
}

const toDateSafe = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate();
  }
  return null;
};

const minutesOf = (d: Date) => d.getHours() * 60 + d.getMinutes();

const timeLabel = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

interface DragState {
  key: string;
  mode: GridCommitMode;
  startClientX: number;
  startClientY: number;
  dayDelta: number;
  minuteDelta: number;
  moved: boolean;
}

const WeekTimeGrid: React.FC<WeekTimeGridProps> = ({
  weekStart,
  schedules,
  employees,
  employeeColors,
  onCommit,
  onEdit,
}) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
      }),
    [weekStart],
  );

  // 첫 렌더 시 8시 위치로 스크롤
  const scrolledRef = useRef(false);
  const setScrollEl = (el: HTMLDivElement | null) => {
    scrollRef.current = el;
    if (el && !scrolledRef.current) {
      el.scrollTop = 8 * HOUR_HEIGHT;
      scrolledRef.current = true;
    }
  };

  // 일정 → 시간 블록(세그먼트) 변환
  const segmentsByDay = useMemo(() => {
    const map = new Map<string, GridSegment[]>();
    days.forEach((d) => map.set(formatLocalDateString(d), []));

    schedules.forEach((schedule) => {
      const dateStr = (schedule.date || '').split('T')[0];
      if (!map.has(dateStr)) return;
      const dayDate = new Date(`${dateStr}T00:00:00`);

      const pushSegment = (seg: 1 | 2, startRaw: unknown, endRaw: unknown) => {
        const start0 = toDateSafe(startRaw);
        const end0 = toDateSafe(endRaw);
        if (!start0 || !end0) return;
        // 시간만 취해 해당 날짜에 부착 (저장된 날짜부와 date 필드 불일치 보호)
        const start = new Date(dayDate);
        start.setHours(start0.getHours(), start0.getMinutes(), 0, 0);
        let end = new Date(dayDate);
        end.setHours(end0.getHours(), end0.getMinutes(), 0, 0);
        let clipped = false;
        if (end.getTime() <= start.getTime()) {
          // 자정 넘김 — 24시까지만 표시
          end = new Date(dayDate);
          end.setHours(23, 59, 0, 0);
          clipped = true;
        }
        map.get(dateStr)!.push({ schedule, seg, start, end, clipped });
      };

      if (schedule.workTime1 || schedule.workTime2) {
        if (schedule.workTime1) pushSegment(1, schedule.workTime1.start, schedule.workTime1.end);
        if (schedule.workTime2) pushSegment(2, schedule.workTime2.start, schedule.workTime2.end);
      } else if (schedule.start_time && schedule.end_time) {
        pushSegment(1, schedule.start_time, schedule.end_time);
      }
    });
    return map;
  }, [schedules, days]);

  const segKey = (s: GridSegment) => `${s.schedule.id}-${s.seg}`;

  /* ── 포인터 드래그 ── */
  const beginDrag = (
    e: React.PointerEvent,
    segment: GridSegment,
    mode: GridCommitMode,
  ) => {
    if (mode === 'resize' && segment.clipped) return; // 야간 잘림 블록은 리사이즈 불가
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({
      key: segKey(segment),
      mode,
      startClientX: e.clientX,
      startClientY: e.clientY,
      dayDelta: 0,
      minuteDelta: 0,
      moved: false,
    });
  };

  const handlePointerMove = (e: React.PointerEvent, segment: GridSegment) => {
    if (!drag || drag.key !== segKey(segment)) return;
    const grid = gridRef.current;
    if (!grid) return;
    const colWidth = (grid.clientWidth - TIME_COL_WIDTH) / 7;
    const dx = e.clientX - drag.startClientX;
    const dy = e.clientY - drag.startClientY;
    const dayDelta = drag.mode === 'move' ? Math.round(dx / colWidth) : 0;
    const minuteDelta = Math.round((dy / HOUR_HEIGHT) * 60 / SNAP_MIN) * SNAP_MIN;
    const moved =
      drag.moved || Math.abs(dx) > CLICK_TOLERANCE || Math.abs(dy) > CLICK_TOLERANCE;
    setDrag({ ...drag, dayDelta, minuteDelta, moved });
  };

  const handlePointerUp = (e: React.PointerEvent, segment: GridSegment) => {
    if (!drag || drag.key !== segKey(segment)) return;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    const { mode, dayDelta, minuteDelta, moved } = drag;
    setDrag(null);

    if (!moved) {
      onEdit(segment.schedule);
      return;
    }

    const durationMin = (segment.end.getTime() - segment.start.getTime()) / 60000;

    if (mode === 'move') {
      if (dayDelta === 0 && minuteDelta === 0) return;
      const startMin = minutesOf(segment.start) + minuteDelta;
      // 0시 ~ (24시 − 근무시간) 범위로 클램프 — 자정 넘김 생성은 폼에서
      const clamped = Math.max(0, Math.min(24 * 60 - durationMin, startMin));
      const newStart = new Date(segment.start);
      newStart.setDate(newStart.getDate() + dayDelta);
      newStart.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMin * 60000);
      onCommit(segment, newStart, newEnd, 'move');
    } else {
      if (minuteDelta === 0) return;
      const endMin = Math.max(
        minutesOf(segment.start) + SNAP_MIN,
        Math.min(24 * 60, minutesOf(segment.end) + minuteDelta),
      );
      const newEnd = new Date(segment.start);
      newEnd.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
      onCommit(segment, segment.start, newEnd, 'resize');
    }
  };

  /* ── 렌더 ── */
  const today = formatLocalDateString(new Date());
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <Box>
      {/* 요일 헤더 */}
      <Box sx={{ display: 'flex', pl: `${TIME_COL_WIDTH}px`, mb: 0.5 }}>
        {days.map((d, i) => {
          const isToday = formatLocalDateString(d) === today;
          return (
            <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: isToday ? 800 : 600,
                  color:
                    i === 0
                      ? 'var(--c-red-600)'
                      : i === 6
                        ? 'var(--c-cool-700)'
                        : isToday
                          ? 'var(--primary)'
                          : 'var(--fg-strong)',
                }}
              >
                {dayNames[i]} {d.getDate()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        ref={setScrollEl}
        sx={{
          maxHeight: 560,
          overflowY: 'auto',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--r-12)',
          background: 'var(--bg-base)',
        }}
      >
        <Box ref={gridRef} sx={{ display: 'flex', position: 'relative' }}>
          {/* 시간 라벨 컬럼 */}
          <Box sx={{ width: TIME_COL_WIDTH, flexShrink: 0 }}>
            {Array.from({ length: 24 }, (_, h) => (
              <Box
                key={h}
                sx={{
                  height: HOUR_HEIGHT,
                  pr: 1,
                  textAlign: 'right',
                  fontSize: 11,
                  color: 'var(--fg-tertiary)',
                  transform: 'translateY(-6px)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {h > 0 ? `${String(h).padStart(2, '0')}:00` : ''}
              </Box>
            ))}
          </Box>

          {/* 요일 컬럼들 */}
          {days.map((d, dayIdx) => {
            const dateStr = formatLocalDateString(d);
            const segs = segmentsByDay.get(dateStr) ?? [];
            const isToday = dateStr === today;
            return (
              <Box
                key={dayIdx}
                sx={{
                  flex: 1,
                  position: 'relative',
                  borderLeft: '1px solid var(--border-subtle)',
                  background: isToday ? 'var(--primary-bg)' : 'transparent',
                  height: 24 * HOUR_HEIGHT,
                  // 시간 눈금
                  backgroundImage:
                    'repeating-linear-gradient(to bottom, transparent, transparent ' +
                    `${HOUR_HEIGHT - 1}px, var(--c-cool-200) ${HOUR_HEIGHT - 1}px, var(--c-cool-200) ${HOUR_HEIGHT}px)`,
                }}
              >
                {segs.map((segment) => {
                  const key = segKey(segment);
                  const dragging = drag?.key === key;
                  const startMin = minutesOf(segment.start);
                  const endMin =
                    segment.end.getHours() === 23 && segment.end.getMinutes() === 59
                      ? 24 * 60
                      : minutesOf(segment.end);

                  // 드래그 중 미리보기 오프셋
                  const previewStart = dragging && drag.mode === 'move' ? startMin + drag.minuteDelta : startMin;
                  const previewEnd =
                    dragging
                      ? drag.mode === 'move'
                        ? endMin + drag.minuteDelta
                        : Math.max(startMin + SNAP_MIN, endMin + drag.minuteDelta)
                      : endMin;
                  const colWidth = 100; // % 기준
                  const xOffset = dragging && drag.mode === 'move' ? drag.dayDelta * colWidth : 0;

                  const color = segment.schedule.employee_id
                    ? employeeColors[segment.schedule.employee_id]
                    : 'var(--c-cool-300)';
                  const textColor = segment.schedule.employee_id
                    ? chipTextColorFor(color)
                    : 'var(--fg-strong)';
                  const name = displayEmployeeName(segment.schedule, employees);

                  return (
                    <Box
                      key={key}
                      onPointerDown={(e) => beginDrag(e, segment, 'move')}
                      onPointerMove={(e) => handlePointerMove(e, segment)}
                      onPointerUp={(e) => handlePointerUp(e, segment)}
                      sx={{
                        position: 'absolute',
                        top: (previewStart / 60) * HOUR_HEIGHT,
                        height: Math.max(18, ((previewEnd - previewStart) / 60) * HOUR_HEIGHT),
                        left: '2px',
                        right: '2px',
                        transform: `translateX(${xOffset}%)`,
                        background: color,
                        color: textColor,
                        borderRadius: 'var(--r-8)',
                        px: 0.75,
                        py: 0.25,
                        fontSize: 11,
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        cursor: dragging ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                        opacity: dragging ? 0.85 : 1,
                        zIndex: dragging ? 10 : 1,
                        boxShadow: dragging ? 'var(--shadow-3)' : 'var(--shadow-1)',
                        transition: dragging ? 'none' : 'box-shadow var(--motion-fast)',
                      }}
                    >
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {name}
                        {segment.seg === 2 && ' (2차)'}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: 'inherit', opacity: 0.9, fontVariantNumeric: 'tabular-nums' }}>
                        {dragging
                          ? `${Math.floor(previewStart / 60)}:${String(previewStart % 60).padStart(2, '0')} - ${Math.floor(previewEnd / 60)}:${String(previewEnd % 60).padStart(2, '0')}`
                          : `${timeLabel(segment.start)} - ${segment.clipped ? '익일' : timeLabel(segment.end)}`}
                      </Typography>

                      {/* 리사이즈 핸들 (하단) — 야간 잘림 블록 제외 */}
                      {!segment.clipped && (
                        <Box
                          onPointerDown={(e) => beginDrag(e, segment, 'resize')}
                          onPointerMove={(e) => handlePointerMove(e, segment)}
                          onPointerUp={(e) => handlePointerUp(e, segment)}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 8,
                            cursor: 'ns-resize',
                            touchAction: 'none',
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Typography sx={{ mt: 1, fontSize: 13, color: 'var(--fg-tertiary)' }}>
        블록을 끌어 시간·요일을 옮기고, 아래 가장자리를 끌어 퇴근 시간을 조절하세요 (30분 단위).
        클릭하면 상세 수정 폼이 열립니다.
      </Typography>
    </Box>
  );
};

export default WeekTimeGrid;
